import React, { useEffect, useState } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

/* ───────────────── GLOBAL ───────────────── */

const GlobalStyle = createGlobalStyle`
  *{
    margin:0;
    padding:0;
    box-sizing:border-box;
    font-family:'Times New Roman', serif;
  }

  body{
    overflow-x:hidden;
    background:#ffffff;           /* Changed from #f4ede2 to white */
  }
`;

/* ───────────────── ANIMATIONS ───────────────── */

const coverOpen = keyframes`
  0%{
    transform:
      rotateY(0deg)
      translateX(0px)
      translateZ(0px);
  }

  20%{
    transform:
      rotateY(-18deg)
      translateX(-2px)
      translateZ(15px);
  }

  45%{
    transform:
      rotateY(-65deg)
      translateX(-6px)
      translateZ(25px);
  }

  70%{
    transform:
      rotateY(-125deg)
      translateX(-10px)
      translateZ(10px);
  }

  100%{
    transform:
      rotateY(-178deg)
      translateX(-4px)
      translateZ(0px);
  }
`;

const coverClose = keyframes`
  0%{
    transform:
      rotateY(-178deg);
  }

  100%{
    transform:
      rotateY(0deg);
  }
`;

const pageFlipForward = keyframes`
  0%{
    transform:rotateY(0deg);
  }

  100%{
    transform:rotateY(-180deg);
  }
`;

const pageFlipBackward = keyframes`
  0%{
    transform:rotateY(-180deg);
  }

  100%{
    transform:rotateY(0deg);
  }
`;

/* ───────────────── PAGE ───────────────── */

const PageContainer = styled.div`
  width:100%;
  min-height:100vh;

  background:#ffffff;             /* Changed from radial-gradient to plain white */

  overflow:hidden;

  padding-top:180px;

  @media(max-width:1400px){
    padding-top:140px;
  }

  @media(max-width:900px){
    padding-top:130px;
  }
`;

const MainLayout = styled.div`
  width:100%;
  max-width:1650px;

  margin:auto;

  display:grid;
  grid-template-columns:340px 1fr;

  align-items:flex-start;

  padding:0 4% 80px;

  @media(max-width:1400px){
    grid-template-columns:280px 1fr;
    padding:0 3% 60px;
  }

  @media(max-width:1100px){
    grid-template-columns:1fr;
  }
`;

/* ───────────────── LEFT CONTENT ───────────────── */

const TextPanel = styled.div`
  position:relative;
  z-index:5;

  padding-top:100px;

  @media(max-width:1400px){
    padding-top:60px;
  }

  @media(max-width:1100px){
    text-align:center;
    padding-top:0;
  }
`;

const Badge = styled.div`
  display:inline-flex;
  align-items:center;
  gap:8px;

  background:#fff;

  padding:10px 18px;

  border-radius:50px;

  border:1px solid rgba(0,0,0,0.08);

  font-size:14px;
  font-weight:700;
  letter-spacing:1px;

  color:#5a3d2b;

  box-shadow:0 10px 20px rgba(0,0,0,0.08);

  @media(max-width:1400px){
    font-size:12px;
    padding:8px 14px;
  }
`;

const Title = styled.h1`
  font-size:76px;
  line-height:0.92;

  margin:24px 0 20px;

  color:#24150e;

  span{
    color:#8d5f3f;
  }

  @media(max-width:1400px){
    font-size:52px;
  }

  @media(max-width:900px){
    font-size:48px;
  }
`;

const Desc = styled.p`
  font-size:19px;
  line-height:1.8;

  color:#5d5248;

  max-width:330px;

  @media(max-width:1400px){
    font-size:16px;
    max-width:280px;
  }

  @media(max-width:1100px){
    max-width:100%;
  }
`;

/* ───────────────── BOOK AREA ───────────────── */

const BookPanel = styled.div`
  width:100%;

  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:flex-start;

  padding-top:60px;

  position:relative;

  @media(max-width:1400px){
    padding-top:30px;
  }

  @media(max-width:1100px){
    padding-top:60px;
  }
`;

const BookWorld = styled.div`
  position:relative;

  perspective:6000px;
  perspective-origin:50% 35%;
`;

const GroundShadow = styled.div`
  position:absolute;

  width:760px;
  height:90px;

  left:50%;
  bottom:-55px;

  transform:translateX(-50%);

  background:
    radial-gradient(
      ellipse,
      rgba(0,0,0,0.30),
      rgba(0,0,0,0.08),
      transparent 75%
    );

  filter:blur(18px);

  z-index:0;

  @media(max-width:1400px){
    width:550px;
    height:70px;
    bottom:-40px;
  }
`;

/* ───────────────── BOOK ───────────────── */

const BookShell = styled.div`
  position:relative;

  width:${p => p.w}px;
  height:${p => p.h}px;

  transform-style:preserve-3d;

  transform:
    rotateX(5deg)
    rotateY(-10deg);
`;

/* ───────────────── PAGES ───────────────── */

const LeftPage = styled.div`
  position:absolute;

  left:0;
  top:0;

  width:${p => p.w / 2}px;
  height:${p => p.h}px;

  overflow:hidden;

  background:#fdf8ef;

  border-radius:14px 0 0 14px;

  box-shadow:
    inset -18px 0 25px rgba(0,0,0,0.12),
    0 10px 24px rgba(0,0,0,0.10);
`;

const RightPage = styled.div`
  position:absolute;

  left:${p => p.w / 2}px;
  top:0;

  width:${p => p.w / 2}px;
  height:${p => p.h}px;

  overflow:hidden;

  background:#fff;

  border-radius:0 14px 14px 0;

  box-shadow:
    inset 18px 0 25px rgba(0,0,0,0.12),
    0 10px 24px rgba(0,0,0,0.10);
`;

const PageImage = styled.img`
  width:100%;
  height:100%;
  object-fit:cover;
`;

const MiddleShadow = styled.div`
  position:absolute;

  top:0;
  bottom:0;
  left:0;

  width:22px;

  background:
    linear-gradient(
      to right,
      rgba(0,0,0,0.14),
      transparent
    );

  z-index:2;

  @media(max-width:1400px){
    width:18px;
  }
`;

const PageCorner = styled.div`
  position:absolute;

  right:0;
  bottom:0;

  width:0;
  height:0;

  border-style:solid;
  border-width:0 0 26px 26px;

  border-color:
    transparent
    transparent
    rgba(0,0,0,0.06)
    transparent;

  @media(max-width:1400px){
    border-width:0 0 20px 20px;
  }
`;

/* ───────────────── PAGE FLIP ───────────────── */

const FlippingPage = styled.div`
  position:absolute;

  left:${p => p.dir === 'forward' ? p.w / 2 : 0}px;
  top:0;

  width:${p => p.w / 2}px;
  height:${p => p.h}px;

  transform-style:preserve-3d;

  transform-origin:
    ${p =>
      p.dir === 'forward'
        ? 'left center'
        : 'right center'};

  animation:
    ${p =>
      p.dir === 'forward'
        ? pageFlipForward
        : pageFlipBackward}
    1.1s cubic-bezier(0.77,0,0.175,1)
    forwards;

  z-index:200;
`;

const PageFace = styled.div`
  position:absolute;
  inset:0;

  overflow:hidden;

  backface-visibility:hidden;
  -webkit-backface-visibility:hidden;

  background:#fff;

  transform:${p => p.back ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

/* ───────────────── COVER ───────────────── */

const CoverFlap = styled.div`
  position:absolute;

  left:0;
  top:0;

  width:${p => p.w / 2}px;
  height:${p => p.h}px;

  transform-style:preserve-3d;
  transform-origin:right center;

  z-index:500;

  cursor:${p => p.bookOpen ? 'default' : 'pointer'};

  transform:
    ${p =>
      p.bookOpen
        ? 'rotateY(-178deg)'
        : 'rotateY(0deg)'};

  animation:
    ${p => {
      if (p.opening) return coverOpen;
      if (p.closing) return coverClose;
      return 'none';
    }}
    1.8s cubic-bezier(0.65,0.02,0.18,1)
    forwards;
`;

const FrontCover = styled.div`
  position:absolute;
  inset:0;

  overflow:hidden;

  border-radius:18px 8px 8px 18px;

  background:
    linear-gradient(
      145deg,
      #714732 0%,
      #5c3928 25%,
      #3b2418 65%,
      #22140d 100%
    );

  backface-visibility:hidden;
  -webkit-backface-visibility:hidden;

  border:1px solid rgba(255,255,255,0.08);

  box-shadow:
    0 35px 70px rgba(0,0,0,0.38),
    inset -14px 0 24px rgba(0,0,0,0.30);

  &::before{
    content:'';

    position:absolute;
    inset:0;

    background:
      repeating-linear-gradient(
        45deg,
        rgba(255,255,255,0.02),
        rgba(255,255,255,0.02) 2px,
        transparent 2px,
        transparent 6px
      );
  }
`;

const Spine = styled.div`
  position:absolute;

  left:0;
  top:0;
  bottom:0;

  width:44px;

  background:
    linear-gradient(
      to right,
      #1d110b,
      #523222,
      #1d110b
    );

  box-shadow:
    inset -8px 0 12px rgba(255,255,255,0.04),
    inset 8px 0 12px rgba(0,0,0,0.5);

  @media(max-width:1400px){
    width:36px;
  }
`;

const GoldLineTop = styled.div`
  position:absolute;

  left:60px;
  right:30px;
  top:48px;

  height:1px;

  background:rgba(255,225,180,0.38);

  @media(max-width:1400px){
    left:50px;
    right:25px;
    top:38px;
  }
`;

const GoldLineBottom = styled.div`
  position:absolute;

  left:60px;
  right:30px;
  bottom:48px;

  height:1px;

  background:rgba(255,225,180,0.38);

  @media(max-width:1400px){
    left:50px;
    right:25px;
    bottom:38px;
  }
`;

const CoverText = styled.div`
  position:absolute;

  left:58px;
  right:28px;
  top:50%;

  transform:translateY(-50%);

  text-align:center;

  z-index:10;

  h2{
    font-size:46px;
    line-height:1;
    color:#f6ead9;
    font-weight:700;
  }

  p{
    margin-top:18px;

    font-size:14px;
    letter-spacing:5px;

    color:#ddb98d;

    text-transform:uppercase;
  }

  @media(max-width:1400px){
    left:48px;
    right:22px;

    h2{
      font-size:36px;
    }

    p{
      font-size:12px;
      margin-top:14px;
      letter-spacing:4px;
    }
  }
`;

const InsideCover = styled.div`
  position:absolute;
  inset:0;

  overflow:hidden;

  background:#f7efe3;

  transform:rotateY(180deg);

  border-radius:0 14px 14px 0;

  backface-visibility:hidden;
  -webkit-backface-visibility:hidden;
`;

/* ───────────────── CONTROLS ───────────────── */

const Controls = styled.div`
  margin-top:45px;

  display:flex;
  align-items:center;
  justify-content:center;
  gap:16px;

  flex-wrap:wrap;

  width:100%;

  @media(max-width:1400px){
    margin-top:30px;
    gap:12px;
  }
`;

const Button = styled.button`
  border:none;

  background:#4a2c1f;
  color:#fff;

  padding:14px 26px;

  border-radius:50px;

  cursor:pointer;

  font-size:15px;
  font-weight:700;

  transition:0.3s ease;

  box-shadow:
    0 10px 20px rgba(0,0,0,0.16);

  &:hover{
    transform:translateY(-2px);
    background:#2b1911;
  }

  &:disabled{
    opacity:0.35;
    cursor:default;
  }

  @media(max-width:1400px){
    padding:12px 22px;
    font-size:14px;
  }
`;

const PageCount = styled.div`
  font-size:18px;
  font-weight:700;

  color:#4c392d;

  strong{
    font-size:26px;
    color:#24150e;
  }

  @media(max-width:1400px){
    font-size:16px;

    strong{
      font-size:22px;
    }
  }
`;

const Hint = styled.div`
  margin-top:28px;

  display:${p => p.show ? 'flex' : 'none'};

  align-items:center;
  justify-content:center;
  gap:10px;

  font-size:18px;
  font-weight:700;

  color:#5d4737;

  width:100%;
  text-align:center;

  @media(max-width:1400px){
    margin-top:18px;
    font-size:16px;
  }
`;

/* ───────────────── IMAGE GALLERY ───────────────── */

const ImageGallery = styled.div`
  display: flex;
  gap: 80px;                       /* Space between images */
  margin-top: 80px;                /* Space above gallery */
  width: 100%;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;

  @media(max-width: 1400px){
    gap: 50px;
    margin-top: 50px;
  }

  @media(max-width: 768px){
    flex-direction: column;
    align-items: center;
    gap: 30px;
  }
`;

const GalleryImage = styled.div`
  width: 45%;                      /* Takes 45% of available width */
  min-width: 400px;                /* Minimum width */
  max-width: 550px;                /* Maximum width */
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 30px 60px rgba(0,0,0,0.25);
  }

  img {
    width: 100%;
    height: auto;
    display: block;
  }

  @media(max-width: 1400px){
    min-width: 320px;
    max-width: 420px;
  }

  @media(max-width: 768px){
    width: 90%;
    min-width: auto;
    max-width: 500px;
  }
`;

/* ───────────────── CTA ───────────────── */

const CTASection = styled.div`
  text-align:center;
  padding:60px 5% 80px;
  background:#ffffff;             /* Add white background */

  @media(max-width:1400px){
    padding:40px 5% 60px;
  }
`;

const CTATitle = styled.h2`
  font-size:56px;
  color:#24150e;

  span{
    color:#8b5e3d;
  }

  @media(max-width:1400px){
    font-size:42px;
  }
`;

const CTADesc = styled.p`
  margin-top:10px;

  font-size:19px;
  color:#5b5148;

  @media(max-width:1400px){
    font-size:16px;
  }
`;

const CTAButton = styled(Link)`
  display:inline-flex;
  align-items:center;
  justify-content:center;

  margin-top:28px;

  background:#4a2c1f;
  color:#fff;

  text-decoration:none;

  padding:16px 36px;

  border-radius:60px;

  font-size:16px;
  font-weight:700;

  transition:0.3s ease;

  &:hover{
    transform:translateY(-3px);
    background:#2d1a12;
  }

  @media(max-width:1400px){
    padding:14px 30px;
    font-size:15px;
  }
`;

/* ───────────────── COMPONENT ───────────────── */

const TestimonialsPage = () => {

  useEffect(() => {
    window.scrollTo(0,0);
  }, []);

  const spreads = [
    {
      left:'/images/book/page1-left.jpg',
      right:'/images/book/page1-right.jpg'
    },
    {
      left:'/images/book/page2-left.jpg',
      right:'/images/book/page2-right.jpg'
    },
    {
      left:'/images/book/page3-left.jpg',
      right:'/images/book/page3-right.jpg'
    },
    {
      left:'/images/book/page4-left.jpg',
      right:'/images/book/page4-right.jpg'
    },
    {
      left:'/images/book/page5-left.jpg',
      right:'/images/book/page5-right.jpg'
    }
  ];

  const [bookOpen, setBookOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState(false);

  const [spreadIdx, setSpreadIdx] = useState(0);

  const [animDir, setAnimDir] = useState(null);
  const [animating, setAnimating] = useState(false);

  const getBookDimensions = () => {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 1400) {
      return { width: 550, height: 362 };
    }
    return { width: 760, height: 500 };
  };

  const [bookDimensions, setBookDimensions] = useState(getBookDimensions());

  useEffect(() => {
    const handleResize = () => {
      setBookDimensions(getBookDimensions());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const BW = bookDimensions.width;
  const BH = bookDimensions.height;

  const current = spreads[spreadIdx];
  const next = spreads[spreadIdx + 1];

  const isFirst = spreadIdx <= 0;
  const isLast = spreadIdx >= spreads.length - 1;

  const openBook = () => {
    if(bookOpen || opening) return;
    setOpening(true);
    setTimeout(() => { setBookOpen(true); },500);
    setTimeout(() => { setOpening(false); },1800);
  };

  const closeBook = () => {
    if(closing || animating) return;
    setClosing(true);
    setTimeout(() => {
      setBookOpen(false);
      setClosing(false);
      setSpreadIdx(0);
    },1200);
  };

  const nextPage = () => {
    if(animating || isLast) return;
    setAnimDir('forward');
    setAnimating(true);
    setTimeout(() => {
      setSpreadIdx(i => i + 1);
      setAnimating(false);
      setAnimDir(null);
    },1100);
  };

  const prevPage = () => {
    if(animating || isFirst) return;
    setAnimDir('backward');
    setAnimating(true);
    setTimeout(() => {
      setSpreadIdx(i => i - 1);
      setAnimating(false);
      setAnimDir(null);
    },1100);
  };

  return (
    <>
      <GlobalStyle />

      <PageContainer>

        <MainLayout>

          {/* LEFT */}

          <TextPanel>
            <Badge>📖 STUDENT STORIES</Badge>
            <Title>Success <span>Stories</span></Title>
            <Desc>Open the book and explore inspiring journeys of our students.</Desc>
          </TextPanel>

          {/* RIGHT */}

          <BookPanel>

            <BookWorld>
              <GroundShadow />
              <BookShell w={BW} h={BH}>

                {bookOpen && !animating && (
                  <>
                    <LeftPage w={BW} h={BH}>
                      <MiddleShadow />
                      <PageImage src={current.left} />
                      <PageCorner />
                    </LeftPage>
                    <RightPage w={BW} h={BH}>
                      <PageImage src={current.right} />
                      <PageCorner />
                    </RightPage>
                  </>
                )}

                {bookOpen && animating && (
                  <FlippingPage dir={animDir} w={BW} h={BH}>
                    <PageFace>
                      {animDir === 'forward'
                        ? <PageImage src={current.right} />
                        : <PageImage src={next?.left} />
                      }
                      <MiddleShadow />
                    </PageFace>
                    <PageFace back>
                      {animDir === 'forward'
                        ? <PageImage src={next?.left} />
                        : <PageImage src={current.right} />
                      }
                    </PageFace>
                  </FlippingPage>
                )}

                <CoverFlap
                  w={BW} h={BH}
                  bookOpen={bookOpen}
                  opening={opening}
                  closing={closing}
                  onClick={!bookOpen ? openBook : undefined}
                >
                  <FrontCover>
                    <Spine />
                    <GoldLineTop />
                    <GoldLineBottom />
                    <CoverText>
                      <h2>Student<br />Success<br />Stories</h2>
                      <p>Learn Thru Us</p>
                    </CoverText>
                  </FrontCover>
                  <InsideCover>
                    <MiddleShadow />
                    <PageImage src={current.left} />
                    <PageCorner />
                  </InsideCover>
                </CoverFlap>

              </BookShell>
            </BookWorld>

            <Hint show={!bookOpen && !opening}>
              👆 Click the Book to Open
            </Hint>

            <Controls>
              {bookOpen && (
                <Button onClick={prevPage} disabled={isFirst || animating}>
                  ← Previous
                </Button>
              )}
              {bookOpen && (
                <PageCount>
                  <strong>{spreadIdx + 1}</strong> / {spreads.length}
                </PageCount>
              )}
              {bookOpen && (
                <Button onClick={nextPage} disabled={isLast || animating}>
                  Next →
                </Button>
              )}
              {bookOpen && (
                <Button onClick={closeBook} disabled={animating}>
                  ✕ Close
                </Button>
              )}
            </Controls>

          </BookPanel>

        </MainLayout>

        {/* IMAGE GALLERY - Outside MainLayout for full width */}

        {/* <ImageGallery>
          <GalleryImage>
            <img src="/images/gallery1.png" alt="Student Success" />
          </GalleryImage>
          <GalleryImage>
            <img src="/images/gallery2.png" alt="Student Achievement" />
          </GalleryImage>
        </ImageGallery> */}

        {/* CTA */}

        <CTASection>
          <CTATitle>Ready to Write Your <span>Success Story?</span></CTATitle>
          <CTADesc>Join thousands of successful certified professionals.</CTADesc>
          <CTAButton to="/contact-us">Begin Your Journey</CTAButton>
        </CTASection>

      </PageContainer>
    </>
  );
};

export default TestimonialsPage;